import { Fragment, useMemo, useState } from "react";
import { IconChevronLeft, IconChevronRight } from "@/components/icons";
import { FieldRenderer } from "../../../components/forms/FieldRenderer";
import { FormShell } from "../../../components/forms/FormShell";
import { ChatbotForm } from "../../form-page/_components/form/ChatbotForm";
import { QuizForm } from "../../form-page/_components/form/QuizForm";
import { builderFieldToFieldDto } from "../../../lib/formBuilder/draft";
import { getPublicFormUrl } from "../../../lib/forms/formUrl";
import { mergeAppearance } from "../../../lib/forms/appearance";
import { isFieldInline } from "../../../lib/forms/fieldOptions";
import type { FormBuilderDraft } from "../../../types/formBuilder";
import type { FieldDto } from "../../../types/field";
import type { FormThemesCatalog } from "../../../types/appearance";
import type { ValuesByName } from "../../../types/visibility";

interface FormPreviewProps {
  draft: FormBuilderDraft;
  themesCatalog: FormThemesCatalog;
  valuesByName: ValuesByName;
  onFieldValueChange: (fieldName: string, value: string) => void;
  formId?: number | null;
  publicId?: string | null;
}

function PreviewSection({
  label,
  labelEmpty,
  showLabel,
  fields,
  introText,
  valuesByName,
  onFieldValueChange,
}: {
  label: string;
  labelEmpty?: boolean;
  showLabel: boolean;
  fields: FieldDto[];
  introText?: string;
  valuesByName: ValuesByName;
  onFieldValueChange: (fieldName: string, value: string) => void;
}) {
  const inlineFields = fields.filter((field) => isFieldInline(field));
  const stackedFields = fields.filter((field) => !isFieldInline(field));

  return (
    <section className="mt-4 first:mt-3">
      {showLabel ? (
        <h2
          className={[
            "form-section__heading font-onest text-sm font-semibold",
            labelEmpty ? "text-slate-400" : "text-ink",
          ].join(" ")}
        >
          {label}
        </h2>
      ) : null}
      <div className="mt-1.5">
        {inlineFields.length > 0 ? (
          <p className="font-onest text-sm leading-relaxed text-ink">
            {introText ? <span>{introText} </span> : null}
            {inlineFields.map((field, index) => (
              <Fragment key={field.name}>
                {index > 0 ? "\u00a0" : null}
                <FieldRenderer
                  field={field}
                  layout="inline"
                  previewMode
                  visible
                  valuesByName={valuesByName}
                  inputRef={() => {}}
                  onFieldValueChange={onFieldValueChange}
                />
              </Fragment>
            ))}
          </p>
        ) : null}

        {stackedFields.length > 0 ? (
          <div className="mt-2 flex flex-col gap-5">
            {stackedFields.map((field) => (
              <FieldRenderer
                key={field.name}
                field={field}
                layout="stacked"
                previewMode
                visible
                valuesByName={valuesByName}
                inputRef={() => {}}
                onFieldValueChange={onFieldValueChange}
              />
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export function FormPreview({
  draft,
  themesCatalog,
  valuesByName,
  onFieldValueChange,
  formId,
  publicId,
}: FormPreviewProps) {
  const [previewStep, setPreviewStep] = useState(0);
  const linkPreviewTitle = draft.name.trim() || "Название формы";
  const linkPreviewUrl =
    formId != null
      ? getPublicFormUrl(publicId ?? formId).replace(/^https?:\/\//, "")
      : null;

  const sections = useMemo(
    () =>
      draft.sections.map((section) => ({
        clientId: section.clientId,
        label: section.label.trim() || "Название раздела",
        labelEmpty: !section.label.trim(),
        fields: section.fields.map((field) =>
          builderFieldToFieldDto(field, formId ?? 0),
        ),
      })),
    [draft.sections, formId],
  );
  const chatbotMode = draft.interactionMode === "chatbot";
  const quizMode = draft.interactionMode === "quiz";
  const stepMode =
    !chatbotMode &&
    !quizMode &&
    draft.sectionDisplayMode === "step" &&
    sections.length > 1;
  const activeStep = Math.min(previewStep, Math.max(0, sections.length - 1));

  const previewAppearance = useMemo(() => {
    if (draft.appearanceCleared) return null;
    return {
      theme: draft.appearanceTheme,
      resolved: mergeAppearance(
        draft.appearanceTheme,
        draft.appearanceOverrides,
        themesCatalog.themes,
      ),
    };
  }, [
    draft.appearanceCleared,
    draft.appearanceTheme,
    draft.appearanceOverrides,
    themesCatalog.themes,
  ]);

  return (
    <aside className="rounded-xl border border-slate-200 bg-slate-50/80 p-3 xl:sticky xl:top-4">
      <p className="mb-2 font-onest text-xs font-semibold uppercase tracking-wide text-slate-500">
        Превью
      </p>
      <div className="mb-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2">
        <p className="font-onest text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          Превью ссылки
        </p>
        <p className="mt-1 truncate font-onest text-sm font-medium text-[#1a0dab]">
          {linkPreviewTitle}
        </p>
        {linkPreviewUrl ? (
          <p className="truncate font-onest text-xs text-slate-500">
            {linkPreviewUrl}
          </p>
        ) : (
          <p className="font-onest text-xs text-slate-400">
            Ссылка появится после сохранения
          </p>
        )}
      </div>
      <FormShell
        appearance={previewAppearance}
        className={
          chatbotMode || quizMode ? "overflow-visible" : "overflow-hidden"
        }
      >
        <div className="form-page-card rounded-lg border border-slate-200 p-4 shadow-sm">
          <h1
            className={[
              "form-page__heading font-unbounded text-lg font-extrabold leading-tight",
              draft.name.trim() ? "text-ink" : "text-slate-400",
            ].join(" ")}
          >
            {draft.name.trim() || "Название формы"}
          </h1>
          {draft.description.trim() &&
          !chatbotMode &&
          !quizMode &&
          !sections.some((s) => s.fields.some((f) => isFieldInline(f))) ? (
            <p className="form-page__description mt-1.5 font-onest text-sm leading-relaxed text-ink-muted">
              {draft.description}
            </p>
          ) : null}

          {chatbotMode ? (
            <div className="mt-3">
              <ChatbotForm
                sections={draft.sections.map((section, index) => ({
                  id: index + 1,
                  form_id: formId ?? 0,
                  label: section.label,
                  order: section.order ?? index,
                  fields: section.fields.map((field) =>
                    builderFieldToFieldDto(field, formId ?? 0),
                  ),
                }))}
                description={draft.description}
                showSectionLabels={draft.showSectionLabels}
                previewMode
              />
            </div>
          ) : null}

          {quizMode ? (
            <div className="mt-3">
              <QuizForm
                sections={draft.sections.map((section, index) => ({
                  id: index + 1,
                  form_id: formId ?? 0,
                  label: section.label,
                  order: section.order ?? index,
                  fields: section.fields.map((field) =>
                    builderFieldToFieldDto(field, formId ?? 0, true),
                  ),
                }))}
                description={draft.description}
                showSectionLabels={draft.showSectionLabels}
                quiz={draft.quiz}
                previewMode
              />
            </div>
          ) : null}

          {stepMode ? (
            <p className="mt-3 font-onest text-xs font-medium text-ink-muted">
              Шаг {activeStep + 1} из {sections.length}
            </p>
          ) : null}

          {chatbotMode || quizMode
            ? null
            : sections.map((section, sectionIndex) => (
            <div
              key={section.clientId}
              className={
                stepMode && sectionIndex !== activeStep ? "hidden" : undefined
              }
            >
              <PreviewSection
                label={section.label}
                labelEmpty={section.labelEmpty}
                showLabel={draft.showSectionLabels}
                fields={section.fields}
                valuesByName={valuesByName}
                onFieldValueChange={onFieldValueChange}
                introText={
                  sectionIndex === 0 &&
                  sections.some((s) => s.fields.some((f) => isFieldInline(f)))
                    ? draft.description.trim()
                    : undefined
                }
              />
            </div>
          ))}

          {stepMode ? (
            <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-3">
              <button
                type="button"
                disabled={activeStep === 0}
                onClick={() => setPreviewStep(activeStep - 1)}
                className="inline-flex items-center gap-1 font-onest text-xs font-medium text-ink-muted disabled:opacity-40"
              >
                <IconChevronLeft size={14} />
                Назад
              </button>
              {activeStep < sections.length - 1 ? (
                <button
                  type="button"
                  onClick={() => setPreviewStep(activeStep + 1)}
                  className="inline-flex items-center gap-1 font-onest text-xs font-medium text-brand-700"
                >
                  Далее
                  <IconChevronRight size={14} />
                </button>
              ) : (
                <button
                  type="button"
                  disabled
                  className="font-onest text-xs font-medium text-brand-700"
                >
                  Отправить
                </button>
              )}
            </div>
          ) : null}

          {draft.sections.every((section) => section.fields.length === 0) ? (
            <p className="mt-4 text-center font-onest text-xs text-slate-400">
              Добавьте поля для превью
            </p>
          ) : null}
        </div>
      </FormShell>
    </aside>
  );
}
